import {
  TAGS,
  type TagExample,
  type TagStats,
  countyRates,
  stateAbbrToFips,
  stateName,
  stateRates,
  tagFamilyLabel,
  tagInfo,
} from '@mollusk/shared';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo, useState } from 'react';
import { TagChip } from '../../components/TagChip';
import { BarChart } from '../../components/charts/BarChart';
import { MONO } from '../../theme';
import { StateTileMap } from './StateTileMap';

type Stats = Omit<TagStats, 'table'>;
const fullName = (st: string) => {
  const f = stateAbbrToFips(st);
  return (f && stateName(f)) ?? st;
};
const pct = (s: number) => (s >= 0.01 ? `${Math.round(s * 100)}%` : `${(s * 100).toFixed(1)}%`);

function Stat({ value, label, tip }: { value: ReactNode; label: string; tip: string }) {
  return (
    <Tooltip title={tip} arrow describeChild>
      <Paper sx={{ p: 2, flex: '1 1 180px', cursor: 'help' }}>
        <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Paper>
    </Tooltip>
  );
}

/** The Tags page (#172), from the precomputed tag stats. Render with
 *  `key={tag}` so switching tags clears the selected state. */
export function TagsView({
  tag,
  stats,
  examples,
  isAdmin,
  onTag,
  onPin,
  onUnpin,
}: {
  tag: string;
  stats: Stats | undefined;
  examples: { pinned: boolean; examples: TagExample[] } | undefined;
  isAdmin: boolean;
  onTag: (tag: string) => void;
  onPin: (permitId: string) => void;
  onUnpin: () => void;
}) {
  const info = tagInfo(tag);
  const s = stats?.tags[tag];
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [pinId, setPinId] = useState('');
  const rates = useMemo(() => (s && stats ? stateRates(s, stats) : []), [s, stats]);
  const topState = rates.find((r) => !r.thin && r.per10k > 0);
  const counties = useMemo(
    () => (s && stats && selectedState ? countyRates(s, stats, selectedState).slice(0, 10) : []),
    [s, stats, selectedState],
  );
  const tableRows = rates.filter((r) => r.n12 > 0);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Tags
      </Typography>
      <Autocomplete
        options={TAGS.map((t) => t.tag)}
        groupBy={(t) => tagFamilyLabel(tagInfo(t)?.family ?? '')}
        value={tag}
        onChange={(_, v) => v && onTag(v)}
        disableClearable
        renderOption={(props, option) => (
          <li {...props} key={option} style={{ fontFamily: MONO, fontSize: 14 }}>
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tag"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
          />
        )}
        sx={{ maxWidth: 420, mb: 2 }}
      />
      {info ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 20 }}>
              {info.tag}
            </Typography>
            <Typography color="text.secondary">{tagFamilyLabel(info.family)}</Typography>
          </Box>
          <Typography sx={{ mt: 1 }}>{info.description}</Typography>
          {info.always_brings.length > 0 ? (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Always brings
              </Typography>
              {info.always_brings.map((b) => (
                <TagChip key={b} tag={b} />
              ))}
            </Box>
          ) : null}
          <Link
            href={`/data-dictionary?tab=tags&tag=${encodeURIComponent(tag)}`}
            variant="body2"
            sx={{ mt: 1, display: 'inline-block' }}
          >
            See it in the data dictionary
          </Link>
        </Paper>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Stat
          value={s && stats?.permits_all ? pct(s.count_all / stats.permits_all) : '—'}
          label="of all permits carry it"
          tip="Permits with this tag ÷ all permits we hold, all years."
        />
        <Stat
          value={s ? s.count_all.toLocaleString() : '—'}
          label="permits with it (all years)"
          tip="Permits carrying the tag, any date."
        />
        <Stat
          value={s ? s.count_12mo.toLocaleString() : '—'}
          label="permits with it in the last 12 months"
          tip="Permits carrying the tag dated in the last 12 months."
        />
        <Stat
          value={topState ? fullName(topState.state) : '—'}
          label="state with the highest rate"
          tip="Highest permits with the tag in the last 12 months per 10,000 residents covered, among states where we cover at least 40% of people."
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          alignItems: 'start',
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>Rate by state, last 12 months</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Permits with the tag per 10,000 residents of the jurisdictions we cover. Click a state.
          </Typography>
          <StateTileMap rows={rates} selected={selectedState} onSelect={setSelectedState} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            A tag's rate depends on what each jurisdiction publishes, and a low rate can mean a
            place permits that work through a separate system.
          </Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          {selectedState ? (
            <>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Top counties in {fullName(selectedState)}
              </Typography>
              {counties.length === 0 ? (
                <Typography color="text.secondary">
                  No permits with this tag in the last 12 months.
                </Typography>
              ) : (
                <Table size="small" aria-label={`Top counties in ${fullName(selectedState)}`}>
                  <TableHead>
                    <TableRow>
                      <TableCell>County</TableCell>
                      <TableCell align="right">Per 10,000</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {counties.map((c) => (
                      <TableRow key={c.fips}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell align="right">{c.per10k.toFixed(1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>By state</Typography>
              <Table size="small" aria-label="Rate by state">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Per 10,000</TableCell>
                    <TableCell align="right">People covered</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(showAll ? tableRows : tableRows.slice(0, 10)).map((r) => (
                    <TableRow key={r.state}>
                      <TableCell>
                        {fullName(r.state)}
                        {r.thin ? (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            (thin coverage)
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{r.per10k.toFixed(1)}</TableCell>
                      <TableCell align="right">{pct(r.share_covered)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {tableRows.length > 10 ? (
                <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ mt: 1 }}>
                  {showAll ? 'Show top 10' : 'Show all states'}
                </Button>
              ) : null}
            </>
          )}
        </Paper>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Permits with {tag}, last 5 years</Typography>
        <BarChart
          title={`Permits tagged ${tag} by year`}
          data={(s?.years ?? []).map(([y, n]) => ({ label: String(y), n }))}
        />
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Example permits</Typography>
        {(examples?.examples ?? []).map((e) => (
          <Box key={e.permit_id} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography>{e.description}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[e.type, [e.city, e.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')} ·{' '}
              <Box component="span" sx={{ fontFamily: MONO, fontSize: 12 }}>
                {e.permit_id}
              </Box>
            </Typography>
          </Box>
        ))}
        {examples && examples.examples.length === 0 ? (
          <Typography color="text.secondary">No example permits for this tag.</Typography>
        ) : null}
        {isAdmin ? (
          <Box
            component="form"
            onSubmit={(ev) => {
              ev.preventDefault();
              if (pinId.trim()) onPin(pinId.trim());
            }}
            sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <TextField
              size="small"
              label="Pin permit_id"
              value={pinId}
              onChange={(e) => setPinId(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Button type="submit" variant="outlined">
              Pin
            </Button>
            {examples?.pinned ? (
              <Button color="error" onClick={onUnpin}>
                Remove pin
              </Button>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Admins only: replace the first example.
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
